import { AssistantType, Namespace, NamespaceType, VendorModel } from '@vendasta/protos/ai_assistants/v1alpha1';
import {
  ASSISTANT_ID_AI_SEARCH_EXPERT,
  ASSISTANT_ID_CHAT_RECEPTIONIST,
  ASSISTANT_ID_CONTENT_CREATOR,
  ASSISTANT_ID_BLOGGER,
  ASSISTANT_ID_MARKETER,
  ASSISTANT_ID_REPUTATION_SPECIALIST,
  ASSISTANT_ID_SALES_COACH,
  ASSISTANT_ID_VOICE_RECEPTIONIST,
  SYSTEM_ASSISTANT_ID,
  VENDASTA_PARTNER,
} from '../ai-assistant.constants';
import { AiVoiceFamily, VoiceLanguage } from '../interfaces/config.interface';

export const VOICE_LANGUAGE_TO_CODE: Record<VoiceLanguage, string> = {
  [VoiceLanguage.ENGLISH]: 'en',
  [VoiceLanguage.ITALIAN]: 'it',
  [VoiceLanguage.SPANISH]: 'es',
  [VoiceLanguage.FRENCH]: 'fr',
  [VoiceLanguage.DUTCH]: 'nl',
  [VoiceLanguage.GERMAN]: 'de',
  [VoiceLanguage.JAPANESE]: 'ja',
};

const VOICE_PREVIEW_BASE_URL = 'https://storage.googleapis.com/voice-receptionist-preview-audios';

function getOpenAIPreviewUrl(name: string): string {
  return `${VOICE_PREVIEW_BASE_URL}/Openai%20audios/${name.toLowerCase()}.wav`;
}

function getVoiceNativeLanguage(voiceValue: string): string {
  const match = voiceValue.match(/-(\w{2})$/);
  return match ? match[1] : 'en';
}

export function getDeepgramPreviewUrl(voiceValue: string, targetLanguage?: string): string {
  const nativeLanguage = getVoiceNativeLanguage(voiceValue);

  if (!targetLanguage) {
    return `${VOICE_PREVIEW_BASE_URL}/Deepgram%20audios/${voiceValue}_${nativeLanguage}.mp3`;
  }

  const supportsTargetLanguage = targetLanguage === 'en' || targetLanguage === nativeLanguage;
  const previewLanguage = supportsTargetLanguage ? targetLanguage : nativeLanguage;

  return `${VOICE_PREVIEW_BASE_URL}/Deepgram%20audios/${voiceValue}_${previewLanguage}.mp3`;
}

function getElevenLabsPreviewUrl(name: string): string {
  return `${VOICE_PREVIEW_BASE_URL}/Elevenlabs%20audios/${name.toLowerCase()}.mp3`;
}

export function convertAssistantTypeToDefaultID(type: AssistantType): string {
  switch (type) {
    case AssistantType.ASSISTANT_TYPE_INBOX:
      return ASSISTANT_ID_CHAT_RECEPTIONIST;
    case AssistantType.ASSISTANT_TYPE_SOCIAL_MARKETING:
      return ASSISTANT_ID_CONTENT_CREATOR;
    case AssistantType.ASSISTANT_TYPE_BLOGGER:
      return ASSISTANT_ID_BLOGGER;
    case AssistantType.ASSISTANT_TYPE_VOICE_RECEPTIONIST:
      return ASSISTANT_ID_VOICE_RECEPTIONIST;
    case AssistantType.ASSISTANT_TYPE_SALES_COACH:
      return ASSISTANT_ID_SALES_COACH;
    case AssistantType.ASSISTANT_TYPE_REPUTATION_MANAGEMENT_REVIEW_AGENT:
      return ASSISTANT_ID_REPUTATION_SPECIALIST;
    case AssistantType.ASSISTANT_TYPE_MARKETER:
      return ASSISTANT_ID_MARKETER;
    case AssistantType.ASSISTANT_TYPE_AEO_AND_SEO_EXPERT:
      return ASSISTANT_ID_AI_SEARCH_EXPERT;
    case AssistantType.ASSISTANT_TYPE_SYSTEM:
      return SYSTEM_ASSISTANT_ID;
    default:
      return 'unspecified';
  }
}

export function AssistantIdToAssistantType(id: string): AssistantType {
  switch (id) {
    case ASSISTANT_ID_CHAT_RECEPTIONIST:
      return AssistantType.ASSISTANT_TYPE_INBOX;
    case ASSISTANT_ID_CONTENT_CREATOR:
      return AssistantType.ASSISTANT_TYPE_SOCIAL_MARKETING;
    case ASSISTANT_ID_BLOGGER:
      return AssistantType.ASSISTANT_TYPE_BLOGGER;
    case ASSISTANT_ID_VOICE_RECEPTIONIST:
      return AssistantType.ASSISTANT_TYPE_VOICE_RECEPTIONIST;
    case ASSISTANT_ID_SALES_COACH:
      return AssistantType.ASSISTANT_TYPE_SALES_COACH;
    case ASSISTANT_ID_REPUTATION_SPECIALIST:
      return AssistantType.ASSISTANT_TYPE_REPUTATION_MANAGEMENT_REVIEW_AGENT;
    case ASSISTANT_ID_AI_SEARCH_EXPERT:
      return AssistantType.ASSISTANT_TYPE_AEO_AND_SEO_EXPERT;
    default:
      return AssistantType.ASSISTANT_TYPE_CUSTOM;
  }
}

const OpenAIVoices = [
  {
    name: 'Cedar',
    value: 'cedar',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getOpenAIPreviewUrl('cedar'),
    recommended: true,
  },
  {
    name: 'Marin',
    value: 'marin',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getOpenAIPreviewUrl('marin'),
    recommended: true,
  },
  {
    name: 'Ash',
    value: 'ash',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getOpenAIPreviewUrl('ash'),
    recommended: true,
  },
  {
    name: 'Alloy',
    value: 'alloy',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getOpenAIPreviewUrl('alloy'),
  },
  {
    name: 'Echo',
    value: 'echo',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getOpenAIPreviewUrl('echo'),
  },
  {
    name: 'Shimmer',
    value: 'shimmer',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getOpenAIPreviewUrl('shimmer'),
  },
  {
    name: 'Ballad',
    value: 'ballad',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.UK',
    previewAudioUrl: getOpenAIPreviewUrl('ballad'),
  },
  {
    name: 'Coral',
    value: 'coral',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getOpenAIPreviewUrl('coral'),
  },
  {
    name: 'Sage',
    value: 'sage',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getOpenAIPreviewUrl('sage'),
  },
  {
    name: 'Verse',
    value: 'verse',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getOpenAIPreviewUrl('verse'),
  },
];
const DeepgramAura2Voices = [
  {
    name: 'Thalia',
    value: 'aura-2-thalia-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
    recommended: true,
  },
  {
    name: 'Andromeda',
    value: 'aura-2-andromeda-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
    recommended: true,
  },
  {
    name: 'Helena',
    value: 'aura-2-helena-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
    recommended: true,
  },
  {
    name: 'Apollo',
    value: 'aura-2-apollo-en',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
    recommended: true,
  },
  {
    name: 'Arcas',
    value: 'aura-2-arcas-en',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
    recommended: true,
  },
  {
    name: 'Aries',
    value: 'aura-2-aries-en',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
    recommended: true,
  },
  {
    name: 'Livia',
    value: 'aura-2-livia-it',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
    languages: [VoiceLanguage.ITALIAN],
    recommended: true,
  },
  {
    name: 'Dionisio',
    value: 'aura-2-dionisio-it',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
    languages: [VoiceLanguage.ITALIAN],
    recommended: true,
  },
  {
    name: 'Celeste',
    value: 'aura-2-celeste-es',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.COLOMBIA',
    languages: [VoiceLanguage.SPANISH],
    recommended: true,
  },
  {
    name: 'Amalthea',
    value: 'aura-2-amalthea-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.PHILIPPINES',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Asteria',
    value: 'aura-2-asteria-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Athena',
    value: 'aura-2-athena-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Atlas',
    value: 'aura-2-atlas-en',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Aurora',
    value: 'aura-2-aurora-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Callista',
    value: 'aura-2-callista-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Cora',
    value: 'aura-2-cora-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Cordelia',
    value: 'aura-2-cordelia-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Delia',
    value: 'aura-2-delia-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Draco',
    value: 'aura-2-draco-en',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.UK',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Electra',
    value: 'aura-2-electra-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Harmonia',
    value: 'aura-2-harmonia-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Hera',
    value: 'aura-2-hera-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Hermes',
    value: 'aura-2-hermes-en',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Hyperion',
    value: 'aura-2-hyperion-en',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.AUSTRALIA',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Iris',
    value: 'aura-2-iris-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Janus',
    value: 'aura-2-janus-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Juno',
    value: 'aura-2-juno-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Jupiter',
    value: 'aura-2-jupiter-en',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Luna',
    value: 'aura-2-luna-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Mars',
    value: 'aura-2-mars-en',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Minerva',
    value: 'aura-2-minerva-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Neptune',
    value: 'aura-2-neptune-en',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Odysseus',
    value: 'aura-2-odysseus-en',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Ophelia',
    value: 'aura-2-ophelia-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Orion',
    value: 'aura-2-orion-en',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Orpheus',
    value: 'aura-2-orpheus-en',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Pandora',
    value: 'aura-2-pandora-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.UK',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Phoebe',
    value: 'aura-2-phoebe-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Pluto',
    value: 'aura-2-pluto-en',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Saturn',
    value: 'aura-2-saturn-en',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Selene',
    value: 'aura-2-selene-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Theia',
    value: 'aura-2-theia-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.AUSTRALIA',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Vesta',
    value: 'aura-2-vesta-en',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Zeus',
    value: 'aura-2-zeus-en',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    languages: [VoiceLanguage.ENGLISH],
  },
  {
    name: 'Cesare',
    value: 'aura-2-cesare-it',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
    languages: [VoiceLanguage.ITALIAN],
  },
  {
    name: 'Elio',
    value: 'aura-2-elio-it',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
    languages: [VoiceLanguage.ITALIAN],
  },
  {
    name: 'Flavio',
    value: 'aura-2-flavio-it',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
    languages: [VoiceLanguage.ITALIAN],
  },
  {
    name: 'Álvaro',
    value: 'aura-2-alvaro-es',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.SPAIN',
    languages: [VoiceLanguage.SPANISH],
  },
  {
    name: 'Agustina',
    value: 'aura-2-agustina-es',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.SPAIN',
    languages: [VoiceLanguage.SPANISH],
  },
  {
    name: 'Hector',
    value: 'aura-2-hector-fr',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.FRANCE',
    languages: [VoiceLanguage.FRENCH],
  },
  {
    name: 'Agathe',
    value: 'aura-2-agathe-fr',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.FRANCE',
    languages: [VoiceLanguage.FRENCH],
  },
  {
    name: 'Julius',
    value: 'aura-2-julius-de',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.GERMANY',
    languages: [VoiceLanguage.GERMAN],
  },
  {
    name: 'Lara',
    value: 'aura-2-lara-de',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.GERMANY',
    languages: [VoiceLanguage.GERMAN],
  },
  {
    name: 'Fujin',
    value: 'aura-2-fujin-ja',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.JAPAN',
    languages: [VoiceLanguage.JAPANESE],
  },
  {
    name: 'Izanami',
    value: 'aura-2-izanami-ja',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.JAPAN',
    languages: [VoiceLanguage.JAPANESE],
  },
  {
    name: 'Ama',
    value: 'aura-2-ama-ja',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.JAPAN',
    languages: [VoiceLanguage.JAPANESE],
  },
  {
    name: 'Sander',
    value: 'aura-2-sander-nl',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.NETHERLANDS',
    languages: [VoiceLanguage.DUTCH],
  },
  {
    name: 'Rhea',
    value: 'aura-2-rhea-nl',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.NETHERLANDS',
    languages: [VoiceLanguage.DUTCH],
  },
];

const ElevenLabsVoices = [
  {
    name: 'Aria',
    value: '9BWtsMINqrJLrRacOk9x',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getElevenLabsPreviewUrl('aria'),
  },
  {
    name: 'Sarah',
    value: 'EXAVITQu4vr4xnSDxMaL',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getElevenLabsPreviewUrl('sarah'),
  },
  {
    name: 'Laura',
    value: 'FGY2WhTYpPnrIDTdsKH5',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getElevenLabsPreviewUrl('laura'),
  },
  {
    name: 'Charlie',
    value: 'IKne3meq5aSn9XLyUdCD',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.AUSTRALIA',
    previewAudioUrl: getElevenLabsPreviewUrl('charlie'),
  },
  {
    name: 'George',
    value: 'JBFqnCBsd6RMkjVDRZzb',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.UK',
    previewAudioUrl: getElevenLabsPreviewUrl('george'),
  },
  {
    name: 'Callum',
    value: 'N2lVS1w4EtoT3dr4eOWO',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getElevenLabsPreviewUrl('callum'),
  },
  {
    name: 'River',
    value: 'SAz9YHcvj6GT2YYXdXww',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getElevenLabsPreviewUrl('river'),
  },
  {
    name: 'Liam',
    value: 'TX3LPaxmHKxFdv7VOQHJ',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getElevenLabsPreviewUrl('liam'),
  },
  {
    name: 'Charlotte',
    value: 'XB0fDUnXU5powFXDhCwa',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.SWEDEN',
    previewAudioUrl: getElevenLabsPreviewUrl('charlotte'),
  },
  {
    name: 'Alice',
    value: 'Xb7hH8MSUJpSbSDYk0k2',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.UK',
    previewAudioUrl: getElevenLabsPreviewUrl('alice'),
  },
  {
    name: 'Matilda',
    value: 'XrExE9yKIg1WjnnlVkGX',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getElevenLabsPreviewUrl('matilda'),
  },
  {
    name: 'Will',
    value: 'bIHbv24MWmeRgasZH58o',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getElevenLabsPreviewUrl('will'),
  },
  {
    name: 'Jessica',
    value: 'cgSgspJ2msm6clMCkdW9',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getElevenLabsPreviewUrl('jessica'),
  },
  {
    name: 'Eric',
    value: 'cjVigY5qzO86Huf0OWal',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getElevenLabsPreviewUrl('eric'),
  },
  {
    name: 'Chris',
    value: 'iP95p4xoKVk53GoZ742B',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getElevenLabsPreviewUrl('chris'),
  },
  {
    name: 'Brian',
    value: 'nPczCjzI2devNBz1zQrb',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getElevenLabsPreviewUrl('brian'),
  },
  {
    name: 'Daniel',
    value: 'onwK4e9ZLuTAKqWW03F9',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.UK',
    previewAudioUrl: getElevenLabsPreviewUrl('daniel'),
  },
  {
    name: 'Lily',
    value: 'pFZP5JQG7iQjIQuC4Bku',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
    previewAudioUrl: getElevenLabsPreviewUrl('lily'),
  },
  {
    name: 'Bill',
    value: 'pqHfZKP75CvOlQylNhV4',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: getElevenLabsPreviewUrl('bill'),
  },
  {
    name: 'Marco',
    value: '13Cuh3NuYvWOVQtLbRN8',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
  },
  {
    name: 'Sami',
    value: 'kAzI34nYjizE0zON6rXv',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
  },
  {
    name: 'Nicola',
    value: 'sKbNSlHXq99bttvf8rRF',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
  },
  {
    name: 'Thomas',
    value: 'CITWdMEsnRduEUkNWXQv',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
  },
  {
    name: 'Adam',
    value: '93nuHbke4dTER9x2pDwE',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.CANADA',
  },
  {
    name: 'Aida',
    value: 'CiwzbDpaN3pQXjTgx3ML',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
  },
  {
    name: 'Mario',
    value: 'tomkxGQGz4b1kE0EM722',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
  },
  {
    name: 'Meera',
    value: 'gCr8TeSJgJaeaIoV4RWH',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
  },
  {
    name: 'Marco',
    value: 'W71zT1VwIFFx3mMGH2uZ',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
  },
  {
    name: 'Carmelo La Rosa',
    value: 'HuK8QKF35exsCh2e7fLT',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
  },
  {
    name: 'Francesco',
    value: 'PJucOeN3PPVPnud4XocS',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
  },
  {
    name: 'Gina',
    value: 'nNt0YcINdGadGcTx5fBM',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
  },
  {
    name: 'Gina',
    value: 'pwvkOXKI34DbjtR6yUk5',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
  },
  {
    name: 'Antonio',
    value: 'iLVmqjzCGGvqtMCk6vVQ',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
  },
  {
    name: 'Danilo',
    value: '4YsN90HrCPrOCmBglwMA',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
  },
  {
    name: 'Brando',
    value: 'o5tUAYEqld5GJZ1Lv8uC',
    gender: 'masculine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
  },
  {
    name: 'Beatrice',
    value: 'UnOINkXZ3yK4vVg3Iayj',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
  },
  {
    name: 'Hope',
    value: 'tnSpp4vdxKPjI9w0GnoV',
    gender: 'feminine',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
  },
];

const deepgramAura2VoiceFamily: AiVoiceFamily = {
  name: 'Deepgram Aura-2',
  provider: VendorModel.VENDOR_MODEL_DEEPGRAM,
  description_key: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.FAMILY_DESCRIPTION.DEEPGRAM_AURA_2',
  voices: DeepgramAura2Voices,
};

const elevenLabsVoiceFamily: AiVoiceFamily = {
  name: 'ElevenLabs Flash v2.5',
  provider: VendorModel.VENDOR_MODEL_ELEVEN_LABS,
  description_key: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.FAMILY_DESCRIPTION.ELEVEN_LABS_FLASH_2_5',
  voices: ElevenLabsVoices,
};

const augustVoiceFamily: AiVoiceFamily = {
  name: 'OpenAI GPT Realtime (1.5)',
  provider: VendorModel.VENDOR_MODEL_OPEN_AI_REALTIME,
  description_key: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.FAMILY_DESCRIPTION.GPT_RT_AUG_2025',
  voices: OpenAIVoices,
};

const openAITTSVoiceFamily: AiVoiceFamily = {
  name: 'OpenAI GPT 4o mini TTS',
  provider: VendorModel.VENDOR_MODEL_OPEN_AI,
  description_key: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.FAMILY_DESCRIPTION.OPENAI_TTS',
  voices: augustVoiceFamily.voices,
};

export function getVoiceFamilies(): AiVoiceFamily[] {
  return [deepgramAura2VoiceFamily, openAITTSVoiceFamily, elevenLabsVoiceFamily, augustVoiceFamily];
}

export function familyFromVoice(voice: string | undefined, vendorModel?: VendorModel) {
  const voiceFamilies = getVoiceFamilies();

  // If vendor model is specified, prioritize families that match that vendor model
  if (vendorModel) {
    const matchingFamily = voiceFamilies.find(
      (family) => family.provider === vendorModel && family.voices.find((v) => v.value === voice),
    );
    if (matchingFamily) {
      return matchingFamily;
    }
  }

  return voiceFamilies.find((family) => family.voices.find((v) => v.value === voice));
}

export function getGenderTranslationKey(gender: string): string {
  if (gender === 'masculine') return 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MASCULINE';
  if (gender === 'feminine') return 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMININE';
  return gender;
}

export function getNamespace(accountGroupId: string | undefined, partnerId: string | undefined): Namespace | undefined {
  if (accountGroupId) {
    return { accountGroupNamespace: { accountGroupId } } as Namespace;
  }
  if (partnerId === VENDASTA_PARTNER) {
    return { systemNamespace: {} } as Namespace;
  }
  if (partnerId) {
    return { partnerNamespace: { partnerId } } as Namespace;
  }
  return;
}

export const globalNamespace: Namespace = { globalNamespace: {} } as Namespace;

export function NamespaceFromTypeAndId(namespaceType: string | NamespaceType, namespaceId: string): Namespace {
  if (!namespaceType || !namespaceId) return { globalNamespace: {} };
  const nsType = NamespaceTypeFromString(namespaceType);

  switch (nsType) {
    case NamespaceType.NAMESPACE_TYPE_ACCOUNT_GROUP:
      return { accountGroupNamespace: { accountGroupId: namespaceId } };
    case NamespaceType.NAMESPACE_TYPE_PARTNER:
      return { partnerNamespace: { partnerId: namespaceId } };
    case NamespaceType.NAMESPACE_TYPE_SYSTEM:
      return { systemNamespace: {} };
    default:
      return { globalNamespace: {} };
  }
}

function NamespaceTypeFromString(namespaceType: string): NamespaceType {
  switch (namespaceType) {
    case 'AccountGroup':
    case NamespaceType.NAMESPACE_TYPE_ACCOUNT_GROUP:
      return NamespaceType.NAMESPACE_TYPE_ACCOUNT_GROUP;
    case 'Partner':
    case NamespaceType.NAMESPACE_TYPE_PARTNER:
      return NamespaceType.NAMESPACE_TYPE_PARTNER;
    case 'System':
    case NamespaceType.NAMESPACE_TYPE_SYSTEM:
      return NamespaceType.NAMESPACE_TYPE_SYSTEM;
    case 'Global':
    case NamespaceType.NAMESPACE_TYPE_GLOBAL:
      return NamespaceType.NAMESPACE_TYPE_GLOBAL;
    default:
      return NamespaceType.NAMESPACE_TYPE_UNSPECIFIED;
  }
}
